import React from 'react';
import { ShoppingBag, Eye, Hand, TrendingUp, Clock } from 'lucide-react';

const ProductInteraction: React.FC = () => {
  const products = [
    { name: 'Women\'s Denim Jacket', views: 145, touches: 67, pickups: 23, conversions: 12, rate: 52 },
    { name: 'Men\'s Casual Shirt', views: 132, touches: 58, pickups: 19, conversions: 8, rate: 42 },
    { name: 'Summer Dress Collection', views: 178, touches: 89, pickups: 34, conversions: 18, rate: 53 },
    { name: 'Athletic Footwear', views: 156, touches: 72, pickups: 28, conversions: 14, rate: 50 }
  ];

  const hotspots = [
    { zone: 'Women\'s New Arrivals', interactions: 234, avgTime: '8m' },
    { zone: 'Sale Rack (Front)', interactions: 198, avgTime: '6m' },
    { zone: 'Accessories Display', interactions: 167, avgTime: '4m' },
    { zone: 'Featured Window Display', interactions: 145, avgTime: '3m' }
  ];

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Product Interaction Analysis
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Track how customers engage with merchandise and displays
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <Eye size={24} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
          <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            611
          </div>
          <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Total Views
          </div>
        </div>
        <div className="card">
          <Hand size={24} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
          <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            286
          </div>
          <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Touch Interactions
          </div>
        </div>
        <div className="card">
          <ShoppingBag size={24} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
          <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            104
          </div>
          <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Product Pickups
          </div>
        </div>
        <div className="card">
          <TrendingUp size={24} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
          <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            49%
          </div>
          <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Avg. Engagement Rate
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Top Performing Products
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--sca-border)' }}>
                <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Product
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Views
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Touches
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Pickups
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Conversions
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Rate
                </th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid var(--sca-border)' }}>
                  <td className="py-3 px-2 text-sm" style={{ color: 'var(--sca-ink)' }}>
                    {product.name}
                  </td>
                  <td className="py-3 px-2 text-sm text-center" style={{ color: 'var(--sca-muted)' }}>
                    {product.views}
                  </td>
                  <td className="py-3 px-2 text-sm text-center" style={{ color: 'var(--sca-muted)' }}>
                    {product.touches}
                  </td>
                  <td className="py-3 px-2 text-sm text-center" style={{ color: 'var(--sca-muted)' }}>
                    {product.pickups}
                  </td>
                  <td className="py-3 px-2 text-sm text-center font-semibold" style={{ color: 'var(--sca-teal)' }}>
                    {product.conversions}
                  </td>
                  <td className="py-3 px-2 text-sm text-center">
                    <span
                      className="px-2 py-1 rounded-full text-xs"
                      style={{
                        backgroundColor: 'var(--sca-badge-bg)',
                        color: 'var(--sca-teal)'
                      }}
                    >
                      {product.rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Interaction Hotspots
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hotspots.map((hotspot, idx) => (
            <div
              key={idx}
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
                    {hotspot.zone}
                  </div>
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--sca-muted)' }}>
                    <Clock size={14} />
                    <span>Avg. time: {hotspot.avgTime}</span>
                  </div>
                </div>
              </div>
              <div className="text-2xl font-bold" style={{ color: 'var(--sca-teal)' }}>
                {hotspot.interactions}
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                interactions today
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductInteraction;
